<script lang="ts" module>
  type SignInForm = {
    email: string;
    phone?: string;
    code?: string;
  };

  const signInDesc: StrictPropertyList<SignInForm> = [
    { k: 'email', t: 'string', st: 'email', req: true, label: 'Email' },
    { k: 'phone', t: 'string', st: 'phone', req: false, label: 'Phone Number' },
    { k: 'code', t: 'string', st: 'numeric', req: false, label: 'Code' },
  ];

  type T = 'sendlink' | 'google' | 'verify';

  const verbosity = 2;
  const debug = true;
</script>

<script lang="ts">
  import { run } from 'svelte/legacy';

  import {
    normalizeConfirmationCode,
    userSignIn,
    userSignInWithPhoneNumber,
  } from '@yp/web.firebase/client/auth.ts';
  import { isFirebaseError } from '@yp/node.apis.gcloud/errors.ts';
  import { assertPresent, checkPresent } from '@yp/common/core.ts';
  import { isPresent, type MapRec } from '@yp/common/identity.ts';
  import { normalizePhoneNumber } from '@yp/common/identity/phone.ts';
  import type { StrictPropertyList } from '@yp/common/schema/descriptor.ts';
  import { Seconds } from '@yp/common/units.ts';
  import { makeCallback, setCallback } from '@yp/web.app/context.ts';
  import {
    period,
    type DialogAction,
    type DialogForm,
    type PromptDict,
  } from '@yp/web.app/gui/modals.ts';
  import type { PromptText } from '@yp/web.app/gui/prompt_modal.ts';
  import { PromptModal } from '@yp/web.app/gui/prompt_modal.ts';
  import { FireContext } from '@yp/web.firebase/context.ts';
  import { RecaptchaVerifier, type ConfirmationResult } from 'firebase/auth';
  import { onMount } from 'svelte';
  import { RecaptchaContainerId, RecaptchaParentId } from './common.ts';

  // Creative Commons Licensed legal examples from Dreamwidth Studios.
  // http://www.dreamwidth.org/legal/tos
  // http://www.dreamwidth.org/legal/privacy

  let confirmation: ConfirmationResult | undefined;

  const cb = setCallback('signIn');
  const notify = makeCallback('toast');

  let auth: FireContext['auth'];
  let resolver: (() => void) | undefined;

  let modal: PromptModal<T, SignInForm> | undefined = $state();
  let form: DialogForm<SignInForm> | undefined = $state();
  let dict: PromptDict | undefined = $state();
  let labels: Partial<MapRec<DialogAction<T>, string>> | undefined = $state();
  let formError: string | undefined = $state();

  run(() => {
    if (verbosity > 1) {
      console.log('SignIn', { value: form?.value, hidden: form?.hidden });
    }
  });

  const header = 'Sign In';

  // TODO!!! This typing doesn't work.
  const text: PromptText = {
    // intro: [
    //   { type: 'text', value: 'Sign In With Google', action: 'google' },
    //   { type: 'text', value: 'or', clazz: 'w-full mx-auto' },
    // ], // "Your Millie's rewards can be redeemed with this number.",
    // intro: "If you don't have an account, we'll create one for you!",
    outro: [], // {
    //   lines: [
    //     [
    //       'By tapping Verify, you are indicating that you accept our',
    //       { type: 'text', value: 'Terms of Service', link: '/legal/tos' },
    //       'and',
    //       { type: 'text', value: 'Privacy Policy', link: '/legal/privacy' },
    //       period(),
    //       'An SMS may be sent. Message & data rates may apply.',
    //     ],
    //   ],
    //   clazz: 'text-xs',
    // },
  };

  const prompts = {
    email: ['Enter your email to request a sign-in link.'],

    phone: [
      // "Your phone number identifies your Millie's account.",
      "If you don't have an account, we'll create one for you!",
    ],

    code: [
      'Enter the 6-digit code we sent to',
      { type: 'id', value: 'phone', clazz: 'text-blue-600' },
      period(),
    ],
  } as const;

  function setRecaptchaVisible(visible: boolean) {
    let prev = 'invisible';
    let next = 'visible';
    if (!visible) {
      [prev, next] = [next, prev];
    }
    const el = document.getElementById(RecaptchaParentId);
    el?.classList.replace(prev, next);
    if (verbosity > 1) {
      console.log('setRecaptchaVisible', { visible, next, className: el?.className });
    }
  }

  function resetRecaptcha(onError?: (error: string) => void) {
    if (verbosity > 1) {
      console.log('resetRecaptcha - clear');
    }
    window.recaptchaVerifier?.clear();
    document.getElementById(RecaptchaParentId)?.replaceChildren();
    const container = document.createElement('div');
    container.setAttribute('id', RecaptchaContainerId);
    document.getElementById(RecaptchaParentId)?.appendChild(container);
    // https://developers.google.com/recaptcha/docs/display#render_param
    window.recaptchaVerifier = new RecaptchaVerifier(auth, RecaptchaContainerId, {
      // N.B. Comment this out to test visible recaptchas.
      size: 'invisible',
      // theme: darkMode ? 'dark' : 'light',
      callback: () => {
        if (verbosity > 0) {
          console.log('Recaptcha solved');
        }
        setRecaptchaVisible(false);
        // https://github.com/firebase/firebase-js-sdk/issues/3356

        // window.recaptchaVerifier?.['recaptcha']?.reset();
      },
      'expired-callback': () => {
        console.warn('Recaptcha expired');
        onError?.('Recaptcha expired. Try again.');
      },
      'error-callback': (error: unknown) => {
        console.warn('Recaptcha error', { error });
        onError?.('Internal error.');
      },
    });
    setRecaptchaVisible(true);
  }

  function clearRecaptcha() {
    setRecaptchaVisible(false);
    if (verbosity > 1) {
      console.log('clearRecaptcha - clear');
    }
    window.recaptchaVerifier?.clear();
    document.getElementById(RecaptchaParentId)?.replaceChildren();
    delete window.recaptchaVerifier;
  }

  function resetInput(params?: { confirm?: ConfirmationResult; error?: string }) {
    if (verbosity > 1) {
      console.log('resetInput', { confirm: params?.confirm !== undefined, error: params?.error });
    }
    confirmation = params?.confirm;
    if (confirmation) {
      assertPresent(form);
      form.value.code = '';
      form.hidden = ['phone'];
      dict = { phone: form.value.phone };
      labels = { verify: 'Verify' };
      formError = undefined;
    } else {
      resetRecaptcha((error) => resetInput({ error }));
      form = {
        desc: signInDesc,
        value: { email: isPresent(params?.error) ? (form?.value.email ?? '') : '', code: 'hidden' },
        prompts,
        hidden: ['phone', 'code'],
      };
      dict = undefined;
      labels = { sendlink: 'Send Link' };
      formError = params?.error;
    }

    form = form;
  }

  async function doVerify() {
    assertPresent(form);
    if (confirmation) {
      form.value.code = normalizeConfirmationCode(form.value.code ?? '');
      if (verbosity > 1) {
        console.log(`Confirming with ${form.value.code}`);
      }
      try {
        const result = await confirmation.confirm(form.value.code);
        if (verbosity > 0) {
          console.log('Confirmation succeeded', { keys: Object.keys(result) });
        }
        resolver?.();
      } catch (error) {
        clearRecaptcha();
        if (isFirebaseError(error) && error.code === 'auth/invalid-verification-code') {
          confirmation = undefined;
          form.hidden = ['code'];
          labels = undefined;
          // DNS: countdown timer.
          formError = 'Invalid code. Click Verify to try again.';
          resetRecaptcha((error) => resetInput({ error }));

          form = form;
        } else {
          console.warn('Confirmation failed', { error });
          resetInput();
        }
      }
    } else {
      form.value.phone = normalizePhoneNumber(form.value.phone ?? '', 'local');
      const signInPhone = normalizePhoneNumber(form.value.phone ?? '', 'e164');

      form = form;
      if (verbosity > 0) {
        console.log(`Signing in with ${signInPhone}`);
      }
      try {
        const confirm = await userSignInWithPhoneNumber(
          auth,
          signInPhone,
          checkPresent(window.recaptchaVerifier),
        );
        if (verbosity > 0) {
          console.log('Sign in succeeded', { confirm });
        }
        resetInput({ confirm });
      } catch (error) {
        const code = isFirebaseError(error) ? error.code : undefined;
        if (code === 'auth/admin-restricted-operation') {
          if (verbosity > 0) {
            console.log('Sign in error', error);
          }
          const result = await userSignIn(signInPhone);
          if (result === 'success') {
            if (verbosity > 1) {
              console.log('Continue sign in.');
            }
            return doVerify();
          } else {
            console.error('Sign in failed', { result });
            resetInput({ error: debug ? 'Sign In failed: ' + String(result) : 'Internal error.' });
          }
        } else if (code === 'auth/too-many-requests') {
          console.error('Sign in failed: Too many retries', { error });
          resetInput({ error: 'Too many retries. Try again later.' });
        } else {
          console.error('Unexpected error', { error });
          resetInput({ error: debug ? 'Unexpected error: ' + String(error) : 'Internal error.' });
        }
      }
    }
  }

  const actions: DialogAction<T>[] = ['sendlink'];
  const actionHandlers = {
    sendlink: async () => {
      assertPresent(form);
      assertPresent(form.value.email);
      await FireContext.signInWithEmail(form.value.email);
      notify({
        title: 'Link Sent!',
        text: 'Check your email to complete login.',
        duration: Seconds(10),
      });
    },
    google: async () => {
      console.log('google');
    },
    verify: () => {
      return doVerify();
    },
  };

  // // DNS: Validate using these.
  // $: isPhoneValid = validatePhoneNumber(form?.value.code ?? '');
  // $: isCodeValid = validateConfirmationCode(form?.value.code ?? '');

  // $: {
  //   if (verbosity > 1) {
  //   console.log('state', { isPhoneValid, isCodeValid });
  //   }
  // }

  onMount(async () => {
    auth = (await FireContext.load()).auth;
    delete window.recaptchaVerifier;

    cb(async () => {
      try {
        await new Promise<void>((resolve) => {
          if (verbosity > 0) {
            console.log('SignIn - open');
          }
          resolver = resolve;
          resetInput();
          modal?.doOpen?.();
          // document.getElementById(signInInputId)?.focus();
        });
      } finally {
        if (verbosity > 0) {
          console.log('SignIn - cleaning up resolver');
        }
        clearRecaptcha();
        modal?.doDismiss?.();
        resolver = undefined;
      }
    });
  });
</script>

<PromptModal
  bind:this={modal}
  {header}
  {text}
  {dict}
  {form}
  {formError}
  {actions}
  {labels}
  {actionHandlers} />
